import { Avatar, CardContent, Grid, List, ListItem, ListItemAvatar, ListItemText, Pagination, Typography } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcCard, MprcContainer, MprcIconButtonDropdown, MprcInfiniteLoading, MprcRequestErrorReport } from "components";
import { MprcFilterCountInfo } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonBack } from "components/iconButtons";
import { IconSort } from "components/icons";
import { TopicSearchDtoPaginatedList, TopicSearchOrderBy } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useGobackOrHome } from "views/common/hooks/useGobackOrHome";
import { GlobalSearchRowsPerPage } from "views/general/GlobalSearch/components/GlobalSearchTabs";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const FullTopicSearch = () => {
    const [lastError, setLastError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [orderBy, setOrderBy] = useState<TopicSearchOrderBy>(TopicSearchOrderBy.Relevance);
    const [globalSearchText, setGlobalSearchText] = useState<string>("");
    const topicResult = useRef<TopicSearchDtoPaginatedList>(null!);

    const { t } = useTranslation("shared");
    const [searchParams] = useSearchParams();
    const api = useCoreApi();
    const navigate = useNavigate();
    const goback = useGobackOrHome();

    ///
    const getState = () => {
        return { pageNumber, orderBy, globalSearchText };
    };

    ///
    async function loadTopics(pageNumber: number) {
        setLoading(true);
        setLastError(null);

        try {
            const response = await api.v1.globalSearch.topicSearch({ searchText: globalSearchText, orderBy, pageNumber, pageSize: GlobalSearchRowsPerPage });
            topicResult.current = response.data;
        }
        catch (e) {
            setLastError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const renderSearch = () => {

        if (loading) return null;

        return topicResult.current.totalRows == 0
            ? <MprcFilterEmptyDataset message={t("GlobalSearch.FullSearch.NoTopicsFound")} />
            : (
                <>
                    <MprcFilterCountInfo
                        text={t("GlobalSearch.FullSearch.TopicsFound", { count: topicResult.current.totalRows })}
                    />

                    <List>
                        {
                            topicResult.current.items.map((v) => {
                                const navUrl = KnowledgePaths.FeedTopic(v.topicId);

                                return (
                                    <ListItem key={v.topicId} disableGutters>
                                        <ListItemAvatar>
                                            <Avatar src={v.thumbnailUrl ?? AppImages.TopicThumbnail} />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Link
                                                    onClick={() => navigate(document.location.search, { replace: true, state: { state: getState() } })}
                                                    to={navUrl}
                                                >
                                                    <Typography color="primary.main" variant="body1">
                                                        {v.name}
                                                    </Typography>
                                                </Link>}
                                            secondary={<Typography variant="subtitle1">{t("GlobalSearch.SearchRenderTopic.Publications", { count: v.totalPublications })}</Typography>}
                                        />
                                    </ListItem>
                                );
                            })
                        }
                    </List>

                    {
                        topicResult.current.totalPages > 1 &&
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={topicResult.current.pageNumber}
                            count={topicResult.current.totalPages}
                            onChange={(e, pn) => setPageNumber(pn)}
                            sx={{ "& ul": { justifyContent: "center" } }}
                        />
                    }
                </>
            );
    };


    /////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        const q = searchParams.get("q");
        setGlobalSearchText(q == null ? "" : decodeURIComponent(q));
        setPageNumber(1);
        setOrderBy(TopicSearchOrderBy.Relevance);
    }, [document.location.search]);


    useDidUpdateEffect(() => {
        loadTopics(pageNumber);
    }, [orderBy, pageNumber, globalSearchText]);

    /////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <>

            <MprcInfiniteLoading visible={loading} />

            {
                lastError
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <MprcContainer>
                            <MprcCard>
                                <MprcCardFilterBox>
                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid>
                                                    <IconButtonBack onClick={goback} />
                                                </Grid>
                                                <Grid>
                                                    <Typography variant="h4">{t("GlobalSearch.FullSearch.TopicTitle")}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid>
                                            <MprcIconButtonDropdown
                                                icon={<IconSort />}
                                                title={t("shared:Filter.OrderBy")}
                                                onChange={(orderBy) => setOrderBy(orderBy)}
                                                selected={orderBy}
                                                menuItemCheck
                                                items={
                                                    [
                                                        { text: t("GlobalSearch.Filters.TopicOrderBy.Relevance"), value: TopicSearchOrderBy.Relevance },
                                                        { text: t("GlobalSearch.Filters.TopicOrderBy.Publications"), value: TopicSearchOrderBy.Publications },
                                                        { text: t("GlobalSearch.Filters.TopicOrderBy.Name"), value: TopicSearchOrderBy.Name },
                                                    ]
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </MprcCardFilterBox>

                                <CardContent>
                                    {renderSearch()}
                                </CardContent>

                            </MprcCard>
                        </MprcContainer>
                    )
            }
        </>
    );
};
